<template>
  <!-- #2092 — the job header's payroll block. Renders nothing at all until the
       server has answered, and nothing ever if this surface requires a saved
       Wijmo workbook and the job has none (Ed, 16:38: the employer sees it
       "ONLY, when a wijmo report is available"). -->
  <div v-if="show" class="jph" data-testid="job-payroll-header">
    <div class="o-common-head">
      <h5 class="c-common-heading">
        <i class="fa fa-file-text-o" aria-hidden="true"></i> Payroll
        <span v-if="isReadOnly" class="jph__ro" data-testid="jph-readonly">read-only</span>
        <!-- #2092 — Ed's mock-up, 2026-10-17: in the title row, to the right of
             "Payroll / READ-ONLY", italic, and ONLY for a linked payroll
             company. His "peature" typo is corrected; the wording is otherwise
             his.

             ⚠ TEXT ONLY. No link, no API key, no activation check — those are
             #2095. The mock also shows "W4 - I9 Complete / Notify those
             missing" and "Download Payroll"; those are NOT built here, because
             Ed has not placed them and host-pm is asking. -->
        <span v-if="isPayrollCompany" class="jph__sales" data-testid="jph-sales">
          A paid feature, please contact CallSteward Sales, for price information on this integration
        </span>
      </h5>
    </div>

    <div class="jph__body">
      <!-- 1 — tear-off tracking, approved / sent. Read from #2093's column; the
           numbers are never recomputed here. -->
      <div class="jph__tile" data-testid="jph-counts">
        <template v-if="data.tearoff">
          <div class="jph__big">
            {{ data.tearoff.up }}<span class="jph__of">/{{ data.tearoff.total }}</span>
          </div>
          <div class="jph__label">Tear Off — Thumbs Up</div>
          <div class="jph__split">
            <span class="jph__up">👍 {{ data.tearoff.up }}</span>
            <span class="jph__down">👎 {{ data.tearoff.down }}</span>
            <!-- Waiting is the people with NO answer — not total minus up, which
                 would count a thumbs-down as still waiting. -->
            <span class="jph__wait">⏳ {{ data.tearoff.waiting }} waiting</span>
          </div>
        </template>
        <template v-else>
          <div class="jph__big jph__muted">—</div>
          <!-- Not "0 of 0": nothing has been sent, which is a different fact
               from a crew that has been asked and said nothing. -->
          <div class="jph__label">No tear-off sent yet</div>
        </template>
      </div>

      <!-- 2 — the last recorded response, in the CLIENT's local time. -->
      <div class="jph__tile" data-testid="jph-last-update">
        <div class="jph__label">Last Update</div>
        <div class="jph__when">
          {{ (data.tearoff && data.tearoff.last_response_local) || 'No responses yet' }}
        </div>
        <div v-if="data.tearoff && data.tearoff.timezone" class="jph__tz">
          {{ data.tearoff.timezone }}
          <!-- Surfaced, not swallowed: a UTC figure labelled as local time is
               the defect behind #1959. -->
          <em v-if="data.tearoff.timezone_fallback">— this client has no timezone set</em>
        </div>
      </div>

      <!-- 3 — the two switches, each showing who and when. -->
      <div class="jph__tile jph__switches">
        <div class="jph__sw" data-testid="jph-steward">
          <label class="jph__swl">
            <input type="checkbox" :checked="approvals.steward_thrown"
                   :disabled="!canThrow('steward') || busy"
                   data-testid="jph-steward-input"
                   @change="toggle('steward', $event.target.checked)">
            <span>Job Steward Approve T/S</span>
          </label>
          <div class="jph__by">{{ byLine(approvals.steward) }}</div>
        </div>

        <div class="jph__sw" data-testid="jph-payroll">
          <label class="jph__swl">
            <input type="checkbox" :checked="approvals.payroll_thrown"
                   :disabled="!canThrow('payroll') || !approvals.payroll_available || busy"
                   data-testid="jph-payroll-input"
                   @change="toggle('payroll', $event.target.checked)">
            <span>Payroll Steward T/S</span>
          </label>
          <div class="jph__by">{{ byLine(approvals.payroll) }}</div>
          <!-- Says WHY it is disabled rather than just being dead. The server
               enforces the order regardless of this hint. -->
          <div v-if="!approvals.payroll_available" class="jph__hint" data-testid="jph-payroll-blocked">
            The Job Steward switch has to be thrown first.
          </div>
          <div v-else-if="!canThrow('payroll') && !isReadOnly" class="jph__hint">
            Only a Payroll Steward for this client can throw this.
          </div>
        </div>
      </div>
      <!-- #2092 — Ed, 2026-10-17 09:34: "please show the future feature in greyed
           out text", laid out as in his mock-up.

           ⚠ INERT BY CONSTRUCTION, not by styling. These are plain <div>s: no
           <a>, no <button>, no href, no @click, no tabindex and no role — so
           there is nothing in the DOM to tab to or activate, and greying them
           is not the only thing stopping someone using them. Their logic and
           activation are undefined and parked on #2095; a disabled button would
           imply a behaviour that does not exist yet.

           Same audience as the Sales line: linked payroll companies only. -->
      <div v-if="isPayrollCompany" class="jph__tile jph__future" data-testid="jph-future-w4">
        <div class="jph__soon">W4 - I9 Complete</div>
        <div class="jph__soon">Notify those missing</div>
      </div>
      <div v-if="isPayrollCompany" class="jph__tile jph__future" data-testid="jph-future-download">
        <div class="jph__soon">Download Payroll</div>
      </div>
    </div>

    <p v-if="error" class="jph__error" data-testid="jph-error">{{ error }}</p>

    <!-- The full history, on demand. Ed asked for who and when on every throw
         and un-throw, which is more than fits beside a switch. -->
    <p v-if="history.length" class="jph__more">
      <a href="javascript: void(0)" data-testid="jph-history-toggle" @click="showHistory = !showHistory">
        {{ showHistory ? 'Hide' : 'Show' }} approval history ({{ history.length }})
      </a>
    </p>
    <table v-if="showHistory && history.length" class="jph__hist" data-testid="jph-history">
      <thead><tr><th>Switch</th><th>Action</th><th>By</th><th>When</th></tr></thead>
      <tbody>
        <tr v-for="(h, i) in history" :key="i">
          <td>{{ h.switch === 'steward' ? 'Job Steward' : 'Payroll Steward' }}</td>
          <td>{{ actionLabel(h) }}</td>
          <td>{{ h.by_name || h.by }}</td>
          <td>{{ localTime(h.at) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import service from "../../services.js";

export default {
  name: "jobPayrollHeader",
  props: {
    jobId: { type: String, required: true },
    headers: { type: Object, default: () => ({}) },
    /** The employer interface never writes (Ed, 16:42). */
    readonly: { type: Boolean, default: false },
    /** Employer only: render nothing unless the job has a saved Wijmo workbook. */
    requireWorkbook: { type: Boolean, default: false },
  },
  data() {
    return { data: null, busy: false, error: "", showHistory: false };
  },
  computed: {
    show() {
      if (!this.data) return false;

      return this.requireWorkbook ? !!this.data.has_workbook : true;
    },
    /**
     * #2092 — from the SERVER, not inferred. `read_only` cannot tell a payroll
     * company from a regular employer: both are read-only with an empty
     * `may_throw`. Only the payroll company sees the activation line.
     */
    isPayrollCompany() {
      return !!(this.data && this.data.viewer_kind === "payroll_company");
    },
    /** The prop OR the server's verdict — either one makes this read-only. */
    isReadOnly() {
      return this.readonly || !!(this.data && this.data.read_only);
    },
    approvals() {
      return (this.data && this.data.approvals) || {};
    },
    history() {
      return (this.approvals.history || []).slice().reverse();   // newest first
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const res = await service.getUrl(
          "/api/v3/payroll/job/" + encodeURIComponent(this.jobId) + "/approvals", this.headers);
        this.data = (res && res.data && res.data.data) || null;
      } catch (e) {
        // A job the caller cannot see, or no payroll block for it. Render
        // nothing rather than an error on a header nobody asked about.
        this.data = null;
      }
    },
    /**
     * ⚠ A HINT, NOT A GATE. The server re-derives authority on every write
     * (the job's steward from jobs.employee_id, a payroll steward from the
     * client's Payroll Steward group). This only decides what to grey out.
     */
    canThrow(which) {
      if (this.readonly) return false;
      // #2092 — the SERVER's own verdict, honoured even if this surface forgot
      // to pass `readonly`. A payroll company reads the block and changes
      // nothing, and that fact travels with the data rather than depending on
      // every mount point remembering it.
      if (this.data && this.data.read_only) return false;

      return ((this.data && this.data.may_throw) || []).indexOf(which) >= 0;
    },
    async toggle(which, checked) {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        const res = await service.postUrl(
          "/api/v3/payroll/job/" + encodeURIComponent(this.jobId) + "/approvals",
          { switch: which, thrown: checked }, this.headers);
        // The write returns the whole header, so there is no second request and
        // no window where the screen disagrees with the server.
        this.data = (res && res.data && res.data.data) || this.data;
      } catch (e) {
        const body = (e && e.response && e.response.data) || {};
        // The server's sentence, never a stringified object — cs3's exception
        // handler returns `error` as an OBJECT on a 500 (#2093 learned this the
        // hard way: "Send FAILED: [object Object]").
        this.error = (typeof body.message === "string" && body.message)
          || (typeof body.error === "string" && body.error)
          || (body.error && typeof body.error === "object" && body.error.message)
          || "That change was not saved.";
        // Put the checkbox back where the server says it is.
        await this.load();
      } finally {
        this.busy = false;
      }
    },
    byLine(state) {
      if (!state || !state.at) return "Not thrown";
      const who = state.by_name || state.by;
      const when = this.localTime(state.at);

      return (state.thrown ? "Thrown by " : "Turned off by ") + who + " · " + when;
    },
    actionLabel(h) {
      if (h.action === "throw") return "Thrown";
      if (h.action === "unthrow") return "Turned off";

      return h.cause === "steward_unthrown" ? "Reset (steward turned theirs off)" : "Reset";
    },
    localTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);

      return isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
    },
  },
};
</script>

<style scoped>
.jph { background: #fff; }
.jph__ro { font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: #7b8794; margin-left: 8px; }
.jph__body { display: flex; flex-wrap: wrap; gap: 16px; padding: 14px 16px; }
.jph__tile { flex: 1 1 200px; min-width: 180px; }
.jph__big { font-size: 30px; font-weight: 700; line-height: 1.1; color: #1f2933; }
.jph__of { font-size: 20px; color: #7b8794; font-weight: 400; }
.jph__muted { color: #9aa5b1; }
.jph__label { font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #7b8794; margin-top: 2px; }
.jph__split { margin-top: 6px; font-size: 13px; display: flex; gap: 10px; flex-wrap: wrap; }
.jph__up { color: #1f7a4d; } .jph__down { color: #8a3b3b; } .jph__wait { color: #7b8794; }
.jph__when { font-size: 15px; font-weight: 600; margin-top: 4px; }
.jph__tz { font-size: 11px; color: #7b8794; margin-top: 2px; }
.jph__tz em { color: #a6763b; font-style: italic; }
.jph__switches { flex: 1 1 320px; }
.jph__sw { margin-bottom: 12px; }
.jph__swl { display: flex; align-items: center; gap: 8px; font-weight: 600; margin: 0; cursor: pointer; }
.jph__swl input[disabled] { cursor: not-allowed; }
.jph__swl input[disabled] + span { color: #9aa5b1; }
.jph__by { font-size: 12px; color: #52606d; margin-left: 24px; }
.jph__hint { font-size: 11px; color: #a6763b; font-style: italic; margin-left: 24px; }
/* #2092 — the future-feature labels. Greyed, italic, and allowed to wrap under
   the block on a narrow screen rather than pushing a horizontal scrollbar. */
.jph__future { flex: 0 1 auto; min-width: 150px; }
.jph__soon {
  color: #9aa5b1; font-style: italic; font-weight: 700; font-size: 14px;
  line-height: 1.5; cursor: default; user-select: none;
}
.jph__error { color: #8a3b3b; font-size: 13px; padding: 0 16px 10px; margin: 0; }
/* The mock-up's line: beside the title, italic, and allowed to wrap on a
   narrow screen rather than pushing the heading out of the row. */
.jph__sales { font-style: italic; font-weight: 700; font-size: 13px; color: #1f2933; margin-left: 14px; }
.jph__more { padding: 0 16px 10px; margin: 0; font-size: 12px; }
.jph__hist { width: 100%; font-size: 12px; border-collapse: collapse; }
.jph__hist th, .jph__hist td { text-align: left; padding: 5px 16px; border-top: 1px solid #e4e7eb; }
.jph__hist th { color: #52606d; font-weight: 600; }
</style>
